import React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface BulkActionButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "ref"> {
  /** Icon component from lucide-react */
  icon: LucideIcon;
  /** Button label text */
  label: string;
  /** Layout direction: vertical (icon above label), horizontal (icon + label inline), or icon (icon only) */
  layout?: "vertical" | "horizontal" | "icon";
  /** "destructive" tints the button red — used by Delete, which sits alone after its own divider. */
  variant?: "default" | "destructive";
}

/** Colour per variant, shared by all three layouts. */
const VARIANT_CLASSES = {
  default: "text-white hover:bg-white/10",
  destructive: "text-red-400 hover:bg-red-500/15 hover:text-red-300",
} as const;

/**
 * BulkActionButton - Atom component for the dark bulk action toolbar.
 *
 * Supports three layouts:
 * - "vertical" (default): icon above label, for action buttons
 * - "horizontal": icon + label inline, for Clear All button
 * - "icon": icon only, for the compact (small-screen) toolbar — the label
 *   still names the button via `aria-label`/`title` so it stays accessible
 *   and tooltip-hoverable.
 *
 * Forwards refs so it can be used as the `asChild` target of Radix
 * primitives (e.g. PopoverTrigger). Also spreads `...rest` onto the
 * underlying <button> so Slot-merged props (aria-*, data-*, the
 * composed onClick) flow through.
 */
export const BulkActionButton = React.memo(
  React.forwardRef<HTMLButtonElement, BulkActionButtonProps>(function BulkActionButton(
    {
      icon: Icon,
      label,
      onClick,
      disabled = false,
      layout = "vertical",
      variant = "default",
      ...rest
    },
    ref
  ) {
    const tint = VARIANT_CLASSES[variant];

    if (layout === "icon") {
      return (
        <button
          {...rest}
          ref={ref}
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          title={label}
          className={cn(
            "flex h-[52px] w-11 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-50",
            tint
          )}
        >
          <Icon className="h-5 w-5" />
        </button>
      );
    }

    if (layout === "horizontal") {
      return (
        <button
          {...rest}
          ref={ref}
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={cn(
            "flex items-center gap-1.5 rounded-lg px-4 py-1.5 transition-colors disabled:opacity-50",
            tint
          )}
        >
          <Icon className="h-4 w-4" />
          <span className="text-xs font-medium">{label}</span>
        </button>
      );
    }

    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "flex h-[52px] shrink-0 flex-col items-center justify-center gap-1 rounded-lg px-3 transition-colors disabled:opacity-50",
          tint
        )}
      >
        <Icon className="h-5 w-5" />
        <span className="whitespace-nowrap text-xs font-medium">{label}</span>
      </button>
    );
  })
);
